"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { AlertCircle, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Button, Field, FieldLabel, Notice } from "@/components/ui";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, rememberMe }),
      });

      if (res.ok) {
        router.push("/");
        router.refresh();
      } else {
        const data = await res.json();
        setError(data.error || "Those credentials were not recognised.");
        setLoading(false);
      }
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      {/* Statement panel — the product says what it is before asking for anything. */}
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-panel border-r border-hairline p-12 xl:p-16">
        <div
          aria-hidden
          className="absolute -top-40 -left-20 size-[520px] rounded-full"
          style={{ background: "radial-gradient(circle, var(--glow), transparent 70%)" }}
        />

        <div className="relative flex items-center gap-2.5">
          <span className="size-9 rounded-xl bg-ink grid place-items-center">
            <svg viewBox="0 0 24 24" className="size-[19px]" fill="none" aria-hidden>
              <path d="M6.5 3.5h7.2L18.5 8v12.5h-12V3.5Z" stroke="var(--canvas)" strokeWidth="1.5" strokeLinejoin="round" opacity="0.45" />
              <path d="M13.5 3.5V8h5" stroke="var(--canvas)" strokeWidth="1.5" strokeLinejoin="round" opacity="0.45" />
              <path d="M4 14.2h4l1.6-3.4 2.4 6 1.7-3.6h6.3" stroke="var(--signal-lit)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="font-display text-[17px] text-ink">Life Care Plan</span>
        </div>

        <div className="relative max-w-md">
          <p className="eyebrow mb-4">Medical record intelligence</p>
          <p className="font-display text-[40px] xl:text-[46px] leading-[1.08] text-ink">
            Hundreds of pages of clinical history, read and structured in seconds.
          </p>
          <p className="text-[14px] text-ink-2 leading-relaxed mt-6">
            Diagnoses with their attributing physician, a treatment timeline, medications and the
            evidence behind each finding — organised for the life care plan.
          </p>
        </div>

        <p className="relative text-[11.5px] text-ink-3 leading-relaxed max-w-sm">
          Confidential patient information. Access is restricted to authorised staff and every
          session is logged.
        </p>
      </section>

      <section className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden flex items-center gap-2.5 mb-10">
            <span className="size-9 rounded-xl bg-ink grid place-items-center">
              <svg viewBox="0 0 24 24" className="size-[19px]" fill="none" aria-hidden>
                <path d="M4 14.2h4l1.6-3.4 2.4 6 1.7-3.6h6.3" stroke="var(--signal-lit)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="font-display text-[17px] text-ink">Life Care Plan</span>
          </div>

          <h1 className="font-display text-[32px] leading-tight text-ink">Sign in</h1>
          <p className="text-[13.5px] text-ink-2 mt-2 mb-8">Continue to the reading room.</p>

          <form onSubmit={handleLogin} className="space-y-5">
            {error && (
              <Notice tone="alert" icon={<AlertCircle className="size-4" />}>
                {error}
              </Notice>
            )}

            <div>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Field
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@practice.com"
              />
            </div>

            <div>
              <FieldLabel htmlFor="password">Password</FieldLabel>
              <div className="relative">
                <Field
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex w-11 cursor-pointer items-center justify-center text-ink-3 hover:text-ink transition-colors"
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="size-4 rounded border-hairline-lit accent-[var(--signal)] cursor-pointer"
              />
              <span className="text-[12.5px] text-ink-2">Stay signed in for 30 days</span>
            </label>

            <Button type="submit" size="lg" loading={loading} className="w-full">
              {loading ? "Signing in…" : "Sign in"}
              {!loading && <ArrowRight className="size-4" />}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
